import type mapboxgl from 'mapbox-gl';
import type { useDispatch } from 'react-redux';

import {
  layerIdSuffix,
  layerSourceType,
  geojsonCustomPropertyName,
  featureCollection,
  DEFAULT_THICKNESS_FOR_LAYER,
  DEFAULT_LAYER_POINT_IMAGE_ID,
  LINE_DASH_ARRAYS_FOR_LAYER,
  LAYER_INTERACTION_STATUS,
} from '@/constants';
import { setTargetLayerStatus } from '@/features/overlay/overlaySlice';
import { serviceProfile as jsonServiceProfile } from '@/profile/serviceProfile';
import type { ServiceProfile } from '@/profile/type';
import type { FeaturesLocation, Location, ToggleDataForLayer } from '@/types';
import { extractPointIconResources } from '@/utils/extractPointIconResourcesForGeojson';
import { getCustomPropertyForGeojson } from '@/utils/getCustomPropertyForGeojson';
import { isDateInPeriod } from '@/utils/isDateInPeriod';
import { loadImages } from '@/utils/loadImage';

export const toggleLayer = (
  { map, overlays }: ToggleDataForLayer,
  serviceProfile: ServiceProfile,
  dispatch: ReturnType<typeof useDispatch>
) => {
  const pointImageIdsWithCenterAnchor = [DEFAULT_LAYER_POINT_IMAGE_ID];
  const loadedImageIds = new Set<string>();
  const pointLayerIds: string[] = [];
  const lineLayerIds: string[] = [];
  const extLayerUrl =
    jsonServiceProfile?.mapConfig?.extLayerUrl ??
    'https://disaportaldata.gsi.go.jp/raster';
  Object.keys(overlays).forEach((key) => {
    const layers = overlays[key].layers;
    layers.forEach((layer) => {
      // レイヤのソース Mapbox
      if (layer.layerSource === layerSourceType.MAPBOX) {
        const ids = layer.id as string[];
        ids.forEach((id) => {
          map.current?.setLayoutProperty(
            id,
            'visibility',
            layer.display ? 'visible' : 'none'
          );
        });
        // レイヤのソース 重ねるハザードマップ
      } else if (layer.layerSource === layerSourceType.DISAPORTALDATA) {
        const ids = layer.id as string[];
        ids.forEach((id) => {
          if (!map.current?.getLayer(id)) {
            map.current?.addSource(id, {
              id: id,
              name: layer.name,
              type: 'raster',
              tiles: [`${extLayerUrl}/${id}/{z}/{x}/{y}.png`],
              maxzoom: serviceProfile.mapConfig.maxZoom,
              minzoom: serviceProfile.mapConfig.minZoom,
            });
            map.current?.addLayer({
              id: id,
              type: 'raster',
              source: id,
              'source-layer': 'sequence',
              layout: {
                visibility: layer.display ? 'visible' : 'none',
              },
            });
            map.current?.setPaintProperty(id, 'raster-opacity', 0.5);
          } else {
            map.current?.setLayoutProperty(
              id,
              'visibility',
              layer.display ? 'visible' : 'none'
            );
          }
        });
      } else if (layer.layerSource === layerSourceType.CUCON) {
        // NOTE: レイヤーに期間表示のvalueが存在する場合表示・非表示のロジックを発火
        const period = layer.period;
        if (period) {
          const type = period.type;
          const value = period.value;
          if (!(isDateInPeriod({ type, value }) ?? false)) return;
        }

        const isNonAttributes = Object.values(layer.attributes)
          .slice(0, 10)
          .every((attr) => attr.value === '' && attr.url === '');
        const id = layer.id as string;
        const lineLayerId = id + layerIdSuffix.LINE;
        const pointLayerId = id + layerIdSuffix.POINT;
        if (!map.current?.getLayer(id)) {
          const location = layer?.location as FeaturesLocation;
          const features = location.features;
          // geojsonのproperties内にカラーのプロパティがある場合はそれを優先（既存のデータとの互換性を考慮）
          const color =
            (getCustomPropertyForGeojson(
              location,
              geojsonCustomPropertyName.color
            ) as string) ?? layer?.color;

          const thickness = Number(
            (getCustomPropertyForGeojson(
              location,
              geojsonCustomPropertyName.thickness
            ) as string) ?? DEFAULT_THICKNESS_FOR_LAYER
          );

          const lineStyle =
            (getCustomPropertyForGeojson(
              location,
              geojsonCustomPropertyName.lineStyle
            ) as number[] | undefined) ?? LINE_DASH_ARRAYS_FOR_LAYER.solid;

          if (layer.location.type === featureCollection) {
            map.current?.addSource(id, {
              type: 'geojson',
              data: {
                type: featureCollection,
                features: features,
              },
            });
          } else {
            const location = layer?.location as Location;
            const geometry = location.geometry;
            map.current?.addSource(id, {
              type: 'geojson',
              data: {
                type: 'Feature',
                geometry: geometry,
                properties: {
                  name: layer.name,
                },
              },
            });
          }

          const makePointLayerInfo = (
            pointLayerId: string,
            id: string,
            display: boolean
          ): mapboxgl.AnyLayer => {
            return {
              id: pointLayerId,
              type: 'circle',
              source: id,
              layout: {
                visibility: display ? 'visible' : 'none',
              },
              // pointデフォルトの色を決める
              paint: { 'circle-radius': 10, 'circle-color': 'red' },
              filter: ['==', '$type', 'Point'],
            };
          };
          // NOTE: featuresにPointが含まれているかどうか
          const containsPointFeatures = features?.some(
            (feature) => feature.geometry.type === 'Point'
          );

          if (containsPointFeatures) {
            const pointFeatures =
              layer?.location !== undefined && 'features' in layer.location
                ? layer?.location?.features.filter(
                    (feature) => feature?.geometry?.type === 'Point'
                  )
                : [];

            if (pointFeatures !== undefined && map.current != null) {
              const pointImageResources =
                extractPointIconResources(pointFeatures);

              // NOTE: pointImageResourcesからidを取り出しして重複を除いた配列を作成
              const filterUnloadedImages = pointImageResources.filter(
                (resource) => !loadedImageIds.has(resource.id)
              );
              const addPointLayer = () => {
                map.current?.addLayer({
                  id: pointLayerId,
                  type: 'symbol',
                  source: id,
                  layout: {
                    visibility: layer.display ? 'visible' : 'none',
                    'icon-image': [
                      'coalesce',
                      ['get', 'customPointImageId'],
                      DEFAULT_LAYER_POINT_IMAGE_ID,
                    ],
                    'icon-anchor': [
                      'match',
                      ['get', 'user_customPointImageId'],
                      pointImageIdsWithCenterAnchor,
                      'center',
                      'bottom',
                    ],
                    'icon-size': 1,
                    'icon-allow-overlap': true,
                    'icon-ignore-placement': true,
                  },
                  paint: {},
                  filter: ['==', '$type', 'Point'],
                });
              };
              if (filterUnloadedImages.length > 0) {
                // NOTE: 同じ画像をmapboxに複数回ロードしないようにするためにloadedImageIdsを更新
                filterUnloadedImages.forEach((resource) =>
                  loadedImageIds.add(resource.id)
                );

                loadImages(map.current, filterUnloadedImages)
                  .then(() => {
                    addPointLayer();
                  })
                  .catch((error) => {
                    console.error('Error loading images:', error);
                  });
              } else {
                addPointLayer();
              }
            } else {
              map.current?.addLayer(
                makePointLayerInfo(pointLayerId, id, layer.display)
              );
            }
          } else {
            map.current?.addLayer(
              makePointLayerInfo(pointLayerId, id, layer.display)
            );
          }

          map.current?.addLayer({
            id: id,
            type: 'fill',
            source: id,
            layout: { visibility: layer.display ? 'visible' : 'none' },
            paint: {
              'fill-color': color ?? 'red',
              'fill-opacity': isNonAttributes ? 0.8 : 0.4,
            },
            filter: ['==', '$type', 'Polygon'],
          });
          map.current?.addLayer({
            id: lineLayerId,
            type: 'line',
            source: id,
            layout: { visibility: layer.display ? 'visible' : 'none' },
            paint: {
              'line-color': color ?? 'red',
              'line-width': thickness,
              'line-dasharray': lineStyle,
              'line-opacity': isNonAttributes ? 0.8 : 0.4,
            },
            filter: ['==', '$type', 'LineString'],
          });
          pointLayerIds.push(pointLayerId);
          lineLayerIds.push(lineLayerId);
          if (!isNonAttributes) {
            const isClicked = () => {
              if (map.current?.getPaintProperty(id, 'fill-opacity') === 0.8) {
                return true;
              } else {
                return false;
              }
            };

            map.current?.on(
              'mouseenter',
              [id, lineLayerId, pointLayerId],
              (e) => {
                const target = e.originalEvent.target;
                const isHoverMarker =
                  target instanceof HTMLElement &&
                  target.classList.contains('marker-img');

                if (map.current) {
                  map.current.getCanvas().style.cursor = 'pointer';

                  if (!isClicked() && !isHoverMarker) {
                    dispatch(
                      setTargetLayerStatus({
                        targetLayerId: id,
                        status: LAYER_INTERACTION_STATUS.HOVER,
                      })
                    );
                    map.current.setPaintProperty(id, 'fill-opacity', 0.6);
                  }
                }
              }
            );

            map.current?.on(
              'mouseleave',
              [id, lineLayerId, pointLayerId],
              () => {
                if (map.current) {
                  map.current.getCanvas().style.cursor = '';
                  if (!isClicked()) {
                    dispatch(
                      setTargetLayerStatus({
                        targetLayerId: null,
                        status: LAYER_INTERACTION_STATUS.NONE,
                      })
                    );
                    map.current.setPaintProperty(id, 'fill-opacity', 0.4);
                  }
                }
              }
            );
          }
        } else {
          map.current?.setLayoutProperty(
            id,
            'visibility',
            layer.display ? 'visible' : 'none'
          );
          map.current?.setLayoutProperty(
            lineLayerId,
            'visibility',
            layer.display ? 'visible' : 'none'
          );
          if (map.current?.getLayer(pointLayerId) !== undefined) {
            map.current?.setLayoutProperty(
              pointLayerId,
              'visibility',
              layer.display ? 'visible' : 'none'
            );
          }
        }
      }
    });
  });
  // NOTE: ポイント・ラインレイヤーをポリゴンレイヤーより前面に表示
  pointLayerIds.forEach((pointLayerId) => {
    if (map.current?.getLayer(pointLayerId)) {
      map.current?.moveLayer(pointLayerId);
    }
  });
  lineLayerIds.forEach((lineLayerId) => {
    map.current?.moveLayer(lineLayerId);
  });
};
